import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import {
  getBalance,
  getDepositAddress,
  requestDeposit,
  requestWithdraw,
  requestPayWithdraw,
  getTransactions,
} from "../lib/api";
import { formatCurrency, formatDate, truncateAddress } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  Copy,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Coins,
} from "lucide-react";

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
};

const statusColors = {
  pending: "text-yellow-500",
  approved: "text-primary",
  rejected: "text-destructive",
};

export default function WalletPage() {
  const { user, updateUser } = useAuth();
  const [depositAddress, setDepositAddress] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showPayWithdrawModal, setShowPayWithdrawModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Deposit form
  const [depositAmount, setDepositAmount] = useState("");
  const [depositTxHash, setDepositTxHash] = useState("");
  const [depositWallet, setDepositWallet] = useState("");

  // Withdraw form
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawWallet, setWithdrawWallet] = useState("");

  // PAY Withdraw form
  const [payWithdrawAmount, setPayWithdrawAmount] = useState("");
  const [payWithdrawWallet, setPayWithdrawWallet] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [balanceRes, addressRes, txRes] = await Promise.all([
        getBalance(),
        getDepositAddress(),
        getTransactions(),
      ]);
      updateUser({ balance: balanceRes.data.balance, pay_balance: balanceRes.data.pay_balance });
      setDepositAddress(addressRes.data);
      setTransactions(txRes.data.transactions);
    } catch (error) {
      console.error("Failed to load wallet data:", error);
    }
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(depositAddress?.wallet_address);
    setCopied(true);
    toast.success("Address copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeposit = async () => {
    if (!depositAmount || !depositTxHash || !depositWallet) {
      toast.error("Please fill all fields");
      return;
    }

    setLoading(true);
    try {
      await requestDeposit({
        amount: parseFloat(depositAmount),
        wallet_address: depositWallet,
        tx_hash: depositTxHash,
      });
      toast.success("Deposit request submitted! Awaiting admin verification.");
      setShowDepositModal(false);
      setDepositAmount("");
      setDepositTxHash("");
      setDepositWallet("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Deposit request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || !withdrawWallet) {
      toast.error("Please fill all fields");
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (amount > user.balance) {
      toast.error("Insufficient balance");
      return;
    }

    setLoading(true);
    try {
      await requestWithdraw({
        amount,
        wallet_address: withdrawWallet,
      });
      toast.success("Withdrawal request submitted! Processing within 24 hours.");
      setShowWithdrawModal(false);
      setWithdrawAmount("");
      setWithdrawWallet("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Withdrawal request failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePayWithdraw = async () => {
    if (!payWithdrawAmount || !payWithdrawWallet) {
      toast.error("Please fill all fields");
      return;
    }

    const amount = parseFloat(payWithdrawAmount);
    if (amount > (user.pay_balance || 0)) {
      toast.error("Insufficient PAY balance");
      return;
    }

    setLoading(true);
    try {
      await requestPayWithdraw({
        amount,
        wallet_address: payWithdrawWallet,
      });
      toast.success("PAY withdrawal request submitted! Processing within 24-48 hours.");
      setShowPayWithdrawModal(false);
      setPayWithdrawAmount("");
      setPayWithdrawWallet("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "PAY withdrawal request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading font-bold mb-2">Wallet</h1>
          <p className="text-muted-foreground">Manage your USDT and PAY coin balances</p>
        </motion.div>

        {/* Balance Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* USDT Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center neon-glow">
                <Wallet className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">USDT Balance</div>
                <div className="text-3xl font-heading font-bold text-primary" data-testid="wallet-balance">
                  {formatCurrency(user?.balance || 0)}
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">TRC20 Network</p>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowDepositModal(true)}
                className="flex-1 neon-glow font-heading font-bold uppercase text-sm"
                data-testid="open-deposit-modal"
              >
                <ArrowDownLeft className="w-4 h-4 mr-2" /> Deposit
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowWithdrawModal(true)}
                className="flex-1 border-primary/50 text-primary hover:bg-primary/10 font-heading font-bold uppercase text-sm"
                data-testid="open-withdraw-modal"
              >
                <ArrowUpRight className="w-4 h-4 mr-2" /> Withdraw
              </Button>
            </div>
          </motion.div>

          {/* PAY Coin Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card rounded-2xl p-6 border-secondary/30"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                <Coins className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">PAY Coin Balance</div>
                <div className="text-3xl font-heading font-bold text-secondary" data-testid="pay-balance">
                  {(user?.pay_balance || 0).toLocaleString()} PAY
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Solana Network (SPL Token)</p>

            <Button
              variant="outline"
              onClick={() => setShowPayWithdrawModal(true)}
              className="w-full border-secondary/50 text-secondary hover:bg-secondary/10 font-heading font-bold uppercase text-sm"
              data-testid="open-pay-withdraw-modal"
            >
              <ArrowUpRight className="w-4 h-4 mr-2" /> Withdraw PAY
            </Button>
          </motion.div>
        </div>

        {/* Deposit Address */}
        {depositAddress && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card rounded-xl p-6 mb-8"
          >
            <h3 className="font-heading font-bold text-lg mb-4">USDT Deposit Address</h3>
            <div className="bg-background/50 rounded-lg p-4 border border-white/10">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-muted-foreground mb-1">
                    {depositAddress.network} - {depositAddress.currency}
                  </div>
                  <div className="font-mono text-sm break-all" data-testid="deposit-address">
                    {depositAddress.wallet_address}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyAddress}
                  className="border-primary/30"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 text-primary" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <p>{depositAddress.instructions}</p>
            </div>
          </motion.div>
        )}

        {/* Transaction History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card rounded-xl p-6"
        >
          <h3 className="font-heading font-bold text-lg mb-4">Transaction History</h3>

          {transactions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => {
                const StatusIcon = statusIcons[tx.status];
                const isDeposit = tx.type === "deposit";
                const isPayWithdraw = tx.type === "pay_withdraw";

                return (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-white/5"
                    data-testid={`transaction-${tx.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isPayWithdraw ? "bg-secondary/20" : isDeposit ? "bg-primary/20" : "bg-accent/20"
                        }`}
                      >
                        {isPayWithdraw ? (
                          <Coins className="w-5 h-5 text-secondary" />
                        ) : isDeposit ? (
                          <ArrowDownLeft className="w-5 h-5 text-primary" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5 text-accent" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium capitalize">
                          {isPayWithdraw ? "PAY Withdraw" : tx.type}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {truncateAddress(tx.wallet_address)}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div
                        className={`font-mono font-bold ${
                          isPayWithdraw ? "text-secondary" : isDeposit ? "text-primary" : "text-accent"
                        }`}
                      >
                        {isDeposit ? "+" : "-"}
                        {isPayWithdraw ? `${tx.amount.toLocaleString()} PAY` : formatCurrency(tx.amount)}
                      </div>
                      <div className={`flex items-center gap-1 text-sm ${statusColors[tx.status]}`}>
                        <StatusIcon className="w-3 h-3" />
                        <span className="capitalize">{tx.status}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* USDT Deposit Modal */}
        <Dialog open={showDepositModal} onOpenChange={setShowDepositModal}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading">Submit USDT Deposit</DialogTitle>
              <DialogDescription>
                After sending USDT, submit the transaction details for verification.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Amount (USDT)</Label>
                <Input
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="100.00"
                  className="bg-background/50 border-white/10 h-12 font-mono"
                  data-testid="deposit-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Your Wallet Address (sender)</Label>
                <Input
                  value={depositWallet}
                  onChange={(e) => setDepositWallet(e.target.value)}
                  placeholder="TRC20 wallet address"
                  className="bg-background/50 border-white/10 h-12 font-mono text-sm"
                  data-testid="deposit-wallet-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Transaction Hash</Label>
                <Input
                  value={depositTxHash}
                  onChange={(e) => setDepositTxHash(e.target.value)}
                  placeholder="Transaction hash from your wallet"
                  className="bg-background/50 border-white/10 h-12 font-mono text-sm"
                  data-testid="deposit-txhash-input"
                />
              </div>

              <Button
                onClick={handleDeposit}
                disabled={loading}
                className="w-full neon-glow font-heading font-bold uppercase"
                data-testid="submit-deposit-btn"
              >
                {loading ? "Submitting..." : "Submit Deposit Request"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* USDT Withdraw Modal */}
        <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading">Request USDT Withdrawal</DialogTitle>
              <DialogDescription>
                Withdrawals are processed within 24 hours after verification.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="text-sm text-muted-foreground">Available USDT Balance</div>
                <div className="text-2xl font-mono font-bold text-primary">
                  {formatCurrency(user?.balance || 0)}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Amount (USDT)</Label>
                <Input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="100.00"
                  max={user?.balance}
                  className="bg-background/50 border-white/10 h-12 font-mono"
                  data-testid="withdraw-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Destination Wallet Address (TRC20)</Label>
                <Input
                  value={withdrawWallet}
                  onChange={(e) => setWithdrawWallet(e.target.value)}
                  placeholder="Your TRC20 USDT wallet address"
                  className="bg-background/50 border-white/10 h-12 font-mono text-sm"
                  data-testid="withdraw-wallet-input"
                />
              </div>

              <Button
                onClick={handleWithdraw}
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 font-heading font-bold uppercase"
                data-testid="submit-withdraw-btn"
              >
                {loading ? "Submitting..." : "Request Withdrawal"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* PAY Coin Withdraw Modal */}
        <Dialog open={showPayWithdrawModal} onOpenChange={setShowPayWithdrawModal}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Coins className="w-5 h-5 text-secondary" />
                Withdraw PAY Coins
              </DialogTitle>
              <DialogDescription>
                PAY coin withdrawals are sent to your Solana wallet within 24-48 hours.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-secondary/10 border border-secondary/20">
                <div className="text-sm text-muted-foreground">Available PAY Balance</div>
                <div className="text-2xl font-mono font-bold text-secondary">
                  {(user?.pay_balance || 0).toLocaleString()} PAY
                </div>
              </div>

              <div className="space-y-2">
                <Label>Amount (PAY)</Label>
                <Input
                  type="number"
                  value={payWithdrawAmount}
                  onChange={(e) => setPayWithdrawAmount(e.target.value)}
                  placeholder="1000"
                  max={user?.pay_balance}
                  className="bg-background/50 border-white/10 h-12 font-mono"
                  data-testid="pay-withdraw-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Solana Wallet Address</Label>
                <Input
                  value={payWithdrawWallet}
                  onChange={(e) => setPayWithdrawWallet(e.target.value)}
                  placeholder="Your Solana wallet address"
                  className="bg-background/50 border-white/10 h-12 font-mono text-sm"
                  data-testid="pay-withdraw-wallet-input"
                />
                <p className="text-xs text-muted-foreground">
                  PAY is an SPL token on Solana. Make sure your wallet supports SPL tokens.
                </p>
              </div>

              <Button
                onClick={handlePayWithdraw}
                disabled={loading}
                className="w-full bg-secondary hover:bg-secondary/90 font-heading font-bold uppercase"
                data-testid="submit-pay-withdraw-btn"
              >
                {loading ? "Submitting..." : "Request PAY Withdrawal"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
